import { isMembership, newClientOfferTag, type PackageProduct } from "@teamna/shared";
import { formatMoney } from "@teamna/web-client/lib/format.ts";
import type { ComponentType } from "react";
import { Badge } from "../components/Badge.tsx";
import { CalendarIcon, LayersIcon, RepeatIcon, ShieldIcon, SparkIcon, TicketIcon } from "../components/Icons.tsx";
import { ListCard, ListRow } from "../components/ListRow.tsx";

/**
 * The packages a member can buy, one row each, in the order the API sends them. The API lists only
 * what this member can buy: an intro offer ("New clients only", migration 0070) comes first and
 * only for a new client, with a "New client offer" tag. The web twin of the phone's package list.
 * Storybook draws it for a new client and for a returning client.
 *
 * Every fact about a package is on its own line with an icon, and nothing is cut short (Add a
 * package workshop, 2026-10-30): a member reads the whole deal before paying. The owner's Add a
 * package dialog draws this same list as its preview.
 */
export function PackageShopList({
  products,
  currency,
  words,
  disabled,
  onBuy,
}: {
  products: PackageProduct[];
  currency: string | undefined;
  /** The business's words: `member` "client" makes "New client offer"; `groups` "classes". */
  words: { member: string; groups: string };
  disabled: boolean;
  onBuy: (product: PackageProduct) => void;
}) {
  return (
    <ListCard>
      {products.map((product) => (
        <ListRow
          key={product.id}
          title={product.name}
          details={
            <ul className="mt-1.5 flex flex-col gap-1">
              {packageFacts(product, words.groups).map((fact) => (
                <li key={fact.text} dir="auto" className="flex items-start gap-2 text-left text-[13px] text-ink-2">
                  <fact.icon size={15} className="mt-0.5 shrink-0 text-ink-3" />
                  <span className="min-w-0">{fact.text}</span>
                </li>
              ))}
            </ul>
          }
          badge={
            product.newClientsOnly ? (
              <Badge tone="confirmed">{newClientOfferTag(words.member)}</Badge>
            ) : isMembership(product) ? (
              <Badge tone="info">Membership</Badge>
            ) : undefined
          }
          value={isMembership(product) ? `${formatMoney(product.priceFils, currency)} a month` : formatMoney(product.priceFils, currency)}
          onSelect={() => onBuy(product)}
          disabled={disabled}
        />
      ))}
    </ListCard>
  );
}

type Fact = { icon: ComponentType<{ size?: number; className?: string }>; text: string };

/** The lines under a package, in the order a member asks: how many, how long, where, cancelling, who. */
export function packageFacts(product: PackageProduct, groups: string): Fact[] {
  const membership = isMembership(product);
  const kind = product.kind === "private" ? "private " : "";
  const sessions = product.unlimited
    ? `Unlimited ${kind}sessions`
    : `${product.credits} ${kind}${product.credits === 1 ? "session" : "sessions"}`;
  const facts: Fact[] = membership
    ? [
        { icon: TicketIcon, text: `${sessions} every ${product.validityDays} days` },
        { icon: RepeatIcon, text: `Renews every ${product.validityDays} days` },
      ]
    : [
        { icon: TicketIcon, text: sessions },
        { icon: CalendarIcon, text: `Use within ${product.validityDays} days of buying` },
      ];
  // A package limited to certain groups has to say so, or a member buys the wrong one and finds out
  // at the booking screen.
  if (product.groupIds && product.groupIds.length > 0) {
    facts.push({ icon: LayersIcon, text: `For selected ${groups.toLowerCase()} only` });
  }
  const cancelling = cancellationTerms(product);
  if (cancelling) facts.push({ icon: ShieldIcon, text: cancelling });
  // An intro offer is bought one time: say so before the member pays.
  if (product.newClientsOnly) facts.push({ icon: SparkIcon, text: "One time only" });
  return facts;
}

/** The package's own cancellation terms, so a member knows them before buying. Null when none are set. */
function cancellationTerms(product: PackageProduct): string | null {
  const free = product.freeCancellations;
  if (free === null) return null;
  if (free === 0) return "No free cancellations";
  const noun = free === 1 ? "cancellation" : "cancellations";
  return product.freeCancellationMode === "late_only" ? `${free} free late ${noun}` : `${free} free ${noun}`;
}
